import { useEffect, useMemo, useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, Cloud, Shield, Globe, Newspaper, Lightbulb,
  Radar, ChevronDown, ChevronRight, Server, Brain, Rocket,
  GitBranch, Users, Wifi, Package, Scale, Radio,
  Activity, FileText, TrendingUp, Building2, Zap, MessageCircle,
  AlertTriangle, Monitor, Plug, LayoutGrid, Skull, Eye, EyeOff, DollarSign, Rss,
  BookOpen, Code, Settings, ClipboardList, Sparkles, Target, Bookmark,
  StickyNote, GitCompare, Columns, Clock, Trophy, Calculator, CalendarClock,
  Grid3x3, Boxes, Timer, Siren, Network, ScrollText, Cable,
  Search, Star, X, GripVertical, Lock, Unlock, RotateCcw, HelpCircle,
  Smartphone, Bug, Key, Database, Award, Gavel, Pencil, Layers, Bot,
  Upload, Webhook, User, Wrench, Crosshair, AlarmClock, ClipboardCheck,
  GitPullRequest, History, Swords,
} from 'lucide-react'
import { clsx } from 'clsx'
import ThemeSwitcher from '../ThemeSwitcher'

export type NavItem = { to: string; label: string; icon: React.ElementType; end?: boolean; keywords?: string; subgroup?: string }
export type NavGroup = { id: string; title: string; items: NavItem[]; defaultOpen?: boolean }

// ── Icon registry keyed by item `to` so we can persist/reload by path ────────
const ICON_BY_TO: Record<string, React.ElementType> = {
  '/app': LayoutDashboard,
  '/app/next-best-action': Target,
  '/app/exec-narrative': FileText,
  '/app/landing': Sparkles,
  '/app/executive': LayoutGrid,
  '/app/for-you': Sparkles,
  '/app/top-stories': Newspaper,
  '/app/briefing': FileText,
  '/app/signals': Radar,
  '/app/what-changed': GitCompare,
  '/app/diff': GitCompare,
  '/app/m':    Smartphone,
  '/app/time-machine': Clock,
  '/app/leavers':      Users,
  '/app/sbom':         Package,
  '/app/dep-confusion':  Package,
  '/app/osv-feed':       Bug,
  '/app/ip-reputation':  Globe,
  '/app/shodan-queries': Eye,
  '/app/dora':           Activity,
  '/app/supply-chain-trust': Shield,
  '/app/detection-coverage': Grid3x3,
  '/app/atomic-red-team':    Target,
  '/app/canarytokens':       Eye,
  '/app/nsm-lens':           Activity,
  '/app/misp-galaxy':        Network,
  '/app/slos':               Activity,
  '/app/chaos':              Zap,
  '/app/drift':              GitCompare,
  '/app/policy':             Scale,
  '/app/idp-risky':          Lock,
  '/app/secret-scans':       Key,
  '/app/runbooks':           BookOpen,
  '/app/tabletops':          Users,
  '/app/bcp':                Boxes,
  '/app/service-readmes':    FileText,
  '/app/data-residency':     Globe,
  '/app/ransomware-geo':     Skull,
  '/app/attack-matrix': Grid3x3,
  '/app/triage':       ClipboardList,
  '/app/status':       Activity,
  '/app/brand':        Eye,
  '/app/sigma':        Code,
  '/app/dns-posture':  Shield,
  '/app/compliance-posture': Scale,
  '/app/oncall':       Users,
  '/app/releases':     GitBranch,
  '/app/ask':          Sparkles,
  '/app/ci':           Activity,
  '/app/patch-tuesday': CalendarClock,
  '/app/vendor-risk':   Building2,
  '/app/credentials':   Lock,
  '/app/services': Boxes,
  '/app/blueprints': Database,
  '/app/scorecards': Award,
  '/app/actions': Zap,
  '/app/designer': LayoutGrid,
  '/app/agent-runs': Sparkles,
  '/app/service-graph': Network,
  '/app/risk-matrix': Grid3x3,
  '/app/postmortems': Siren,
  '/app/decisions': ScrollText,
  '/app/countdowns': Timer,
  '/app/incidents': AlertTriangle,
  '/app/agents': Sparkles,
  '/app/ai-insights': Brain,
  '/app/ai-models': Trophy,
  '/app/ai-costs': Calculator,
  '/app/ai-adoption': Brain,
  '/app/super-trends': Sparkles,
  '/app/trends': Sparkles,
  '/app/trend-charts': Sparkles,
  '/app/hype-cycle': Activity,
  '/app/tech-radar': Target,
  '/app/watchlist': Eye,
  '/app/vendor-comparison': Columns,
  '/app/eol': CalendarClock,
  '/app/competitors': Building2,
  '/app/sentiment': MessageCircle,
  '/app/sentiment-tracking': TrendingUp,
  '/app/tech-news': Newspaper,
  '/app/cyber': Shield,
  '/app/cyber-warroom': Skull,
  '/app/world': Globe,
  '/app/regulatory': Scale,
  '/app/compliance': Scale,
  '/app/cloud': Cloud,
  '/app/internet-health': Wifi,
  '/app/outage-history': Clock,
  '/app/supply-chain': Package,
  '/app/energy': Zap,
  '/app/innovation': Lightbulb,
  '/app/startups': Rocket,
  '/app/manda': GitBranch,
  '/app/opensource': GitBranch,
  '/app/pricing': DollarSign,
  '/app/pricing-trends': TrendingUp,
  '/app/earnings': Clock,
  '/app/talent': Users,
  '/app/terminal': Monitor,
  '/app/workspace': LayoutGrid,
  '/app/bookmarks': Bookmark,
  '/app/notes': StickyNote,
  '/app/connectors-manage': Cable,
  '/app/connectors-health': Activity,
  '/app/ransomware': Skull,
  '/app/exposure': Eye,
  '/app/threat-actors': Users,
  '/app/cdm/attribution': Crosshair,
  '/app/cdm/regulation-timers': AlarmClock,
  '/app/cdm/connectivity': Network,
  '/app/cdm/privacy': Shield,
  '/app/cdm/replay': History,
  '/app/threat-globe': Globe,
  '/app/war-room-globe': Globe,
  '/app/changelog': GitBranch,
  '/app/help': HelpCircle,
  '/app/eol-manage': CalendarClock,
  '/app/feed-manager': Rss,
  '/app/admin/feeds': Rss,
  '/app/integrations': Plug,
  '/app/wiki': BookOpen,
  '/app/api-docs': Code,
  '/app/admin': Users,
  '/app/admin/access': Lock,
  '/app/admin/jml': Users,
  '/app/admin/orgs': Building2,
  '/app/admin/system-health': Activity,
  '/app/me/access': User,
  '/app/settings': Settings,
  '/app/activity': ClipboardList,
}

// ── Default inventory — DevSecOps + GUI-driven grouping ──────────────────────
// Order reflects how a CTO/CISO actually opens this dashboard:
//   1. Overview (what's the world like right now?)
//   2. Cyber & Threat (the biggest concern)
//   3. Risk & Compliance (board-level worry)
//   4. Engineering Ops (the work)
//   5. AI (active fast-moving area)
//   6. Intelligence & Innovation (industry awareness)
//   7. Trends & Radar (analysis lenses)
//   8. Cloud & Infra (the substrate)
//   9. Workspace (personal productivity)
//  10. Admin & System (back-office)
export const DEFAULT_GROUPS: NavGroup[] = [
  {
    id: 'overview', title: 'Overview', defaultOpen: true, items: [
      { to: '/app',                label: 'Command Centre',    icon: LayoutDashboard, end: true },
      { to: '/app/next-best-action', label: 'Next Best Action', icon: Target, keywords: 'next best action recommended prioritised what to do now nba triage urgency tte deadline patch contain rotate comply ranked synthesis' },
      { to: '/app/exec-narrative', label: 'Board Pack', icon: FileText, keywords: 'executive narrative board pack ciso summary posture report export markdown weekly brief synthesis trend bottom line' },
      { to: '/app/landing',        label: 'My Landing',        icon: Sparkles, keywords: 'persona role-based view cto ciso compliance soc devops curated landing chooser switch role' },
      { to: '/app/executive',      label: 'Executive Summary', icon: LayoutGrid, keywords: 'connectors tiles rollup mobile m phone pocket board' },
      { to: '/app/briefing',       label: 'Daily Briefing',    icon: FileText },
      { to: '/app/for-you',        label: 'For You',           icon: Sparkles },
      { to: '/app/top-stories',    label: 'Top Stories',       icon: Newspaper },
      { to: '/app/war-room-globe', label: 'Situation-Room Globe', icon: Globe, keywords: 'display lobby tv kiosk fullscreen ticker headline war room' },
      { to: '/app/diff',           label: 'Weekly Diff',       icon: GitCompare, keywords: 'cve kev ransomware vendor advisories week over week delta' },
      { to: '/app/what-changed',   label: 'What Changed',      icon: GitCompare },
    ],
  },
  {
    id: 'cyber', title: 'Cyber & Threat', defaultOpen: false, items: [
      // ─── Situation Room ───
      { to: '/app/cyber-warroom',      label: 'Cyber Situation Room', icon: Skull, subgroup: 'Situation Room', keywords: 'threat intel iocs war room' },
      { to: '/app/cyber-news',         label: 'Cyber Threat News',  icon: Newspaper, subgroup: 'Situation Room', keywords: 'security articles bleepingcomputer krebs hacker news' },
      { to: '/app/threat-globe',       label: 'Threat Globe',       icon: Globe, subgroup: 'Situation Room', keywords: 'full screen actors 3d 2d map' },
      { to: '/app/cyber',              label: 'Cyber Security',     icon: Shield, subgroup: 'Situation Room' },

      // ─── Threat Intelligence ───
      { to: '/app/threat-actors',      label: 'Threat Actors',      icon: Users, subgroup: 'Threat Intelligence', keywords: 'mitre misp galaxy apt adversary catalog' },
      { to: '/app/cdm/attribution',    label: 'Attribution Lens',   icon: Crosshair, subgroup: 'Threat Intelligence', keywords: 'cdm threat actor attribution lens sector banking ttp mitre exposure ciso morning summary axonius cannot render track 3.14' },
      { to: '/app/misp-galaxy',        label: 'MISP Galaxy',        icon: Network, subgroup: 'Threat Intelligence', keywords: 'misp galaxy threat actor cluster ransomware tool malpedia synonyms' },
      { to: '/app/attack-matrix',      label: 'ATT&CK Matrix',      icon: Grid3x3, subgroup: 'Threat Intelligence', keywords: 'mitre techniques tactics heatmap navigator' },
      { to: '/app/ransomware',         label: 'Ransomware Tracker', icon: Skull, subgroup: 'Threat Intelligence', keywords: 'leak site victims gangs' },
      { to: '/app/ransomware-geo',     label: 'Ransomware Geo',     icon: Skull, subgroup: 'Threat Intelligence', keywords: 'ransomware geo sector country victims overlay map heatmap by country sector pivot' },

      // ─── Vulnerabilities ───
      { to: '/app/triage',             label: 'Vuln Triage',        icon: ClipboardList, subgroup: 'Vulnerabilities', keywords: 'cve owner remediation backlog' },
      { to: '/app/zero-day-clock',     label: 'Zero Day Clock',     icon: Clock, subgroup: 'Vulnerabilities', keywords: 'tte time to exploit kev cisa median decay zerodayclock' },
      { to: '/app/exploitation-race',  label: 'Exploitation Race',  icon: Timer, subgroup: 'Vulnerabilities', keywords: 'time to exploit tte mttr remediation race velocity epss kev predicted exploitation window losing race patch before weaponized mythos ai discovered compensating controls blast radius' },
      { to: '/app/patch-tuesday',      label: 'Patch Tuesday',      icon: CalendarClock, subgroup: 'Vulnerabilities', keywords: 'microsoft msrc monthly patches' },
      { to: '/app/osv-feed',           label: 'OSV.dev Feed',       icon: Bug, subgroup: 'Vulnerabilities', keywords: 'osv cross ecosystem vuln npm pypi go maven crates rubygems nuget malicious' },
      { to: '/app/dep-confusion',      label: 'Dep Confusion',      icon: Package, subgroup: 'Vulnerabilities', keywords: 'typosquat npm pypi lookalike package name confusion supply chain' },
      { to: '/app/sbom',               label: 'SBOM Scanner',       icon: Package, subgroup: 'Vulnerabilities', keywords: 'cyclonedx spdx software bill materials kev epss' },

      // ─── Exposure & Posture ───
      { to: '/app/exposure',           label: 'External Exposure',  icon: Eye, subgroup: 'Exposure & Posture', keywords: 'subdomain ct log shadow it hibp osv' },
      { to: '/app/dns-posture',        label: 'DNS / TLS Posture',  icon: Shield, subgroup: 'Exposure & Posture', keywords: 'dmarc spf dkim dnssec hsts cert chain grade' },
      { to: '/app/compliance-posture', label: 'Detection Coverage', icon: Scale, subgroup: 'Exposure & Posture', keywords: 'iso 27001 nist csf soc2 attack mapping coverage' },
      { to: '/app/secret-scans',       label: 'Secrets Sprawl',     icon: Key, subgroup: 'Exposure & Posture', keywords: 'gitleaks trufflehog detect-secrets sprawl trend findings repo scan' },
      { to: '/app/cdm/shadow-saas',    label: 'Shadow SaaS',        icon: Cloud, subgroup: 'Exposure & Posture', keywords: 'cdm shadow saas casb sso egress entra sign-in dns netflow zeek slack notion figma monday airtable github gitlab jira okta auth0 stripe brex ramp workday bamboohr greenhouse lever lattice loom miro linear datadog new relic splunk sentry pagerduty zendesk freshdesk intercom box dropbox onedrive google drive microsoft 365 zoom teams webex chatgpt openai anthropic claude perplexity huggingface cursor cohere mistral managed sso_gap shadow_saas unknown sanctioned dismissed track 2.9' },
      { to: '/app/brand',              label: 'Brand Monitor',      icon: Eye, subgroup: 'Exposure & Posture', keywords: 'mention search company product news paste' },
      { to: '/app/ip-reputation',      label: 'IP Reputation',      icon: Globe, subgroup: 'Exposure & Posture', keywords: 'greynoise abuseipdb classification noise riot block trusted' },
      { to: '/app/shodan-queries',     label: 'Shodan Dorks',       icon: Eye, subgroup: 'Exposure & Posture', keywords: 'shodan saved query board exposed services etcd kubernetes mongodb redis rdp' },

      // ─── Detection & Hunting ───
      { to: '/app/sigma',              label: 'Sigma Rules',        icon: Code, subgroup: 'Detection & Hunting', keywords: 'detection siem splunk sentinel rules library' },
      { to: '/app/detection-coverage', label: 'Detection Coverage', icon: Grid3x3, subgroup: 'Detection & Hunting', keywords: 'sigma yara att&ck heatmap mitre coverage map detection as code dac' },
      { to: '/app/atomic-red-team',    label: 'Atomic Red Team',    icon: Target, subgroup: 'Detection & Hunting', keywords: 'atomic red team red canary purple test detection validation att&ck' },
      { to: '/app/purple-range',       label: 'Purple Range',       icon: Swords, subgroup: 'Detection & Hunting', keywords: 'purple team red blue breach attack simulation BAS adversary emulation kill chain mitre attack defend detection coverage exercise range cymulate safebreach attackiq caldera' },
      { to: '/app/nsm-lens',           label: 'NSM Log Lens',       icon: Activity, subgroup: 'Detection & Hunting', keywords: 'suricata zeek bro eve json conn.log notice.log nsm network monitoring ids' },
      { to: '/app/canarytokens',       label: 'Canarytokens',       icon: Eye, subgroup: 'Detection & Hunting', keywords: 'honeytoken honeypot canary trip canarytokens.org thinkst bait fake credentials' },

      // ─── Attack Path & Toxic ───
      { to: '/app/attack-paths',       label: 'Attack Paths',       icon: Network, subgroup: 'Attack Path & Toxic', keywords: 'attack path toxic combination chain caasm ransomware rdp mfa kev internet exposed admin grant compromised credential eol os stale ssh key api token edr blind spot reactflow graph step entry privilege impact exfil control gap track 6d' },
      { to: '/app/ai-vuln-discovery',  label: 'AI Vuln Discovery',  icon: Sparkles, subgroup: 'Attack Path & Toxic', keywords: 'mythos big sleep xbow ai discovered vulnerability zero-day llm reasoning toxic combination novel chain frontier model defender naptime codemender autonomous pentest surge curve reasoned finding review queue adversarial refuter' },
      { to: '/app/cdm/correlations',   label: 'CDM Correlations',   icon: Network, subgroup: 'Attack Path & Toxic', keywords: 'cdm correlation engine cross domain join threat actor cve kev ransomware article frontier incident dora ict orphan nhi planner critic agentic ratify intelligence layer moat axonius caasm cannot do this track 3.11' },
      { to: '/app/cdm/connectivity',   label: 'CDM Connectivity',   icon: Network, subgroup: 'Attack Path & Toxic', keywords: 'cdm network connectivity declared rules observed flows netflow vpc flow zeek suricata firewall acl policy blast radius shadow path unused permission confirmed f5 cisco palo alto fortinet aws sg azure nsg k8s networkpolicy istio authorizationpolicy whitespace axonius wiz cannot do this track 2.8' },

      // ─── Insurance & Underwriting ───
      { to: '/app/underwriting',       label: 'Underwriting Pulse', icon: DollarSign, subgroup: 'Insurance & Underwriting', keywords: 'cyber insurance underwriting carrier coalition at-bay at bay resilience cowbell corvus beazley travelers stance threat insights ransomware bec wire fraud lockbit blackcat akira claims loss experience priced advisory feed signal pulse rss hourly insurtech actuarial' },

      // ─── Supply Chain ───
      { to: '/app/supply-chain-trust', label: 'Supply-Chain Trust', icon: Shield, subgroup: 'Supply Chain', keywords: 'sigstore cosign rekor slsa intoto attestation provenance grade' },
    ],
  },
  {
    id: 'risk', title: 'Risk & Compliance', items: [
      { to: '/app/risk-matrix',       label: 'Risk Matrix',     icon: Grid3x3 },
      { to: '/app/vendor-risk',       label: 'Vendor Risk',     icon: Building2, keywords: 'bitsight scorecard supply chain third party passive recon dns breach' },
      { to: '/app/vendor-comparison', label: 'Vendor Compare',  icon: Columns },
      { to: '/app/compliance',        label: 'Compliance',      icon: Scale },
      { to: '/app/regulatory',        label: 'Regulatory Radar',icon: Scale },
      { to: '/app/cdm/regulation-timers', label: 'Regulation Timers', icon: AlarmClock, keywords: 'cdm regulation timers dora ict nis2 sec 8-k cyber disclosure pra ss 1/21 incident reporting deadline countdown initial notification 4 hour 24 hour 72 hour 30 days 4 business days early warning final report root cause uk eu us banking regulatory clock track 3.13' },
      { to: '/app/eol',               label: 'EOL / Evergreen', icon: CalendarClock, keywords: 'end of life upgrade' },
      { to: '/app/target-state',      label: 'Distance to Target', icon: Target, keywords: 'how far from N target state version gap behind versions generations EOL evergreen upgrade drift desired state distance to N current vs target latest stable lts major model registry deepseek postgres node python evergreen on target' },
      { to: '/app/credentials',       label: 'Credential Expiry', icon: Lock, keywords: 'api keys certs secrets rotation expiring' },
      { to: '/app/idp-risky',         label: 'IdP Risky Sign-ins', icon: Lock, keywords: 'entra azure ad risky users sign-in failures dormant service principals graph' },
      { to: '/app/data-residency',    label: 'Data Residency',     icon: Globe, keywords: 'sovereignty residency map gdpr schrems region cloud uk eu data classification regulated' },
      { to: '/app/cdm/privacy',       label: 'CDM Privacy',        icon: Shield, keywords: 'cdm privacy gdpr article 17 erasure right to be forgotten purge cascade field sensitivity column masking pii classification public restricted confidential secret unmask role scope audit track 4.17 banking dora subject access request lawful basis court order retention' },
      { to: '/app/watchlist',         label: 'Watchlist',       icon: Eye },
    ],
  },
  {
    id: 'engineering', title: 'Engineering Ops', defaultOpen: false, items: [
      // ─── Core CDM ───
      { to: '/app/cdm',           label: 'Common Data Model',  icon: Database, subgroup: 'Core CDM', keywords: 'cdm asset graph identity resolution survivorship event-sourced golden record provenance lineage caasm mdm cmdb fellegi sunter crdt' },
      { to: '/app/cdm/users',            label: 'CDM Users',            icon: Users, subgroup: 'Core CDM', keywords: 'cdm users persons accounts identities nhi service account workload upn entra hr feed' },
      { to: '/app/cdm/software',         label: 'CDM Software',         icon: Layers, subgroup: 'Core CDM', keywords: 'cdm software inventory vendors products versions installations cpe purl eol cve sam asset management' },
      { to: '/app/cdm/network',          label: 'CDM Network',          icon: Network, subgroup: 'Core CDM', keywords: 'cdm network f5 cisco palo alto fortinet virtual server pool firewall policy declared connectivity exposed ports' },
      { to: '/app/cdm/specs',            label: 'CDM Adapter Specs',    icon: Code, subgroup: 'Core CDM', keywords: 'cdm adapter specs generic json yaml long tail snipe-it manageengine custom no code declarative jsonpath field map' },
      { to: '/app/cdm/import',           label: 'CDM File Import',      icon: Upload, subgroup: 'Core CDM', keywords: 'cdm file import csv xlsx excel upload spreadsheet hr feed leavers asset register flat file dump bmc helix sccm export staging absorb' },
      { to: '/app/cdm/webhooks',         label: 'CDM Webhooks',         icon: Webhook, subgroup: 'Core CDM', keywords: 'cdm webhook ingest push hmac signature servicenow jira datadog github app inbound x-signature shared secret track 1.3' },

      // ─── Adapters & Pipelines ───
      { to: '/app/cdm/adapters',         label: 'CDM Adapters',         icon: Activity, subgroup: 'Adapters & Pipelines', keywords: 'cdm adapters registry health crowdstrike defender intune entra f5 tenable kubernetes run pull manual trigger source' },
      { to: '/app/cdm/policy-proposals', label: 'CDM Policy Proposals', icon: Brain, subgroup: 'Adapters & Pipelines', keywords: 'cdm policy proposal tuning agent planner critic survivorship review approve reject ai suggested human overridden adr-0006' },
      { to: '/app/cdm/queries',          label: 'CDM Saved Queries',    icon: Search, subgroup: 'Adapters & Pipelines', keywords: 'cdm saved queries dsl json filter aggregation editor preview library' },
      { to: '/app/cdm/dashboards',       label: 'CDM Dashboards',       icon: Grid3x3, subgroup: 'Adapters & Pipelines', keywords: 'cdm dashboards saved queries tiles grafana grid layout visualisation count timeseries pie heatmap dsl' },

      // ─── Operations ───
      { to: '/app/cdm/enforcement',      label: 'CDM Enforcement',      icon: Zap,    subgroup: 'Operations', keywords: 'cdm enforcement bulk action saved query fan out webhook abort dry run track 2.7 invocation' },
      { to: '/app/cdm/compliance',       label: 'CDM Compliance',       icon: Scale,  subgroup: 'Operations', keywords: 'cdm compliance cis nist iso v8 critical security controls scorecard pass fail rules dora' },
      { to: '/app/cdm/nhis',             label: 'CDM NHIs (orphans)',   icon: Bot,    subgroup: 'Operations', keywords: 'cdm nhi non human identity service account workload identity orphan github app k8s service account agent classifier reverse attestation' },
      { to: '/app/cdm/report',           label: 'CDM Daily Report',     icon: FileText, subgroup: 'Operations', keywords: 'cdm daily report rollup markdown export download send slack discord teams webhook digest' },
      { to: '/app/cdm/frontier-overlay', label: 'CDM Frontier-AI Overlay', icon: Brain, subgroup: 'Operations', keywords: 'cdm frontier ai overlay risk vendor exposure capability moment jailbreak safety incident model release openai anthropic google mistral meta xai cohere bedrock azure ml pipeline rag agent runtime aiid track 3.12 axonius cannot' },

      // ─── Change & Remediation ───
      { to: '/app/cdm/remediation',      label: 'CDM Remediation',      icon: Wrench, subgroup: 'Change & Remediation', keywords: 'cdm remediation auto ticket servicenow jira webhook tagback push tag back github kubernetes vault entra cmdb bmc helix dry run write back queue track 6a soft' },
      { to: '/app/cdm/changes',          label: 'CDM Change Pipeline',  icon: GitPullRequest, subgroup: 'Change & Remediation', keywords: 'cdm change pipeline proposal active remediation crowdstrike isolate contain quarantine f5 rule cisco acl github pat revoke vault tag two key approval rollback maintenance window track 6b moonshot' },
      { to: '/app/cdm/gitops',           label: 'CDM GitOps for Security', icon: GitBranch, subgroup: 'Change & Remediation', keywords: 'cdm gitops firewall as code yaml pull request risk envelope drift detector reconciliation pr f5 cisco palo alto panorama kubernetes networkpolicy track 6c moonshot' },

      // ─── Time Travel ───
      { to: '/app/cdm/replay',           label: 'CDM Replay (time travel)', icon: History, subgroup: 'Time Travel', keywords: 'cdm replay time travel point in time as of bitemporal observed recorded event firehose audit diff state projection golden record what did we know breach forensics track 4.16' },
      { to: '/app/services',      label: 'Service Catalog',    icon: Boxes },
      { to: '/app/blueprints',    label: 'Blueprints',         icon: Database, keywords: 'port catalog entities schema' },
      { to: '/app/scorecards',    label: 'Scorecards',         icon: Award, keywords: 'port maturity gold silver bronze rubric rules' },
      { to: '/app/actions',       label: 'Self-service',       icon: Zap, keywords: 'port actions forms approvals workflow' },
      { to: '/app/designer',      label: 'Dashboard Designer', icon: LayoutGrid, keywords: 'port interface widgets custom dashboard' },
      { to: '/app/agent-runs',    label: 'Agent Runs',         icon: Sparkles, keywords: 'port autonomous agent plan execute' },
      { to: '/app/service-graph', label: 'Service Graph',      icon: Network },
      { to: '/app/incidents',     label: 'External Incidents', icon: AlertTriangle, keywords: 'cloud outages cves' },
      { to: '/app/postmortems',   label: 'Postmortem Log',     icon: Siren, keywords: 'incidents mttr' },
      { to: '/app/decisions',     label: 'Decision Log',       icon: ScrollText, keywords: 'adr architecture' },
      { to: '/app/countdowns',    label: 'Countdown Board',    icon: Timer, keywords: 'patch tuesday' },
      { to: '/app/oncall',        label: 'On-call Rotation',   icon: Users, keywords: 'pagerduty rotation shift schedule' },
      { to: '/app/releases',      label: 'GH Releases',        icon: GitBranch, keywords: 'github oss dependencies kubernetes openssl postgres react' },
      { to: '/app/ci',            label: 'CI Status',          icon: Activity, keywords: 'github actions workflows ci cd build pipeline' },
      { to: '/app/dora',          label: 'DORA Four Keys',     icon: Activity, keywords: 'deploy frequency lead time change failure mttr elite high medium low' },
      { to: '/app/slos',          label: 'SLO Burn Board',     icon: Activity, keywords: 'slo error budget burn rate sli prometheus pyrra sloth nines availability' },
      { to: '/app/chaos',         label: 'Chaos Experiments',  icon: Zap, keywords: 'chaos mesh litmus game day blast radius hypothesis resilience testing' },
      { to: '/app/drift',         label: 'Config Drift',       icon: GitCompare, keywords: 'driftctl terraform plan iac infrastructure drift detection runtime expected actual' },
      { to: '/app/policy',        label: 'Policy as Code',     icon: Scale, keywords: 'opa conftest checkov trivy kubescape kyverno policy findings rego' },
      { to: '/app/runbooks',      label: 'Runbook Library',    icon: BookOpen, keywords: 'runbook ir incident response markdown freshness last tested overdue' },
      { to: '/app/tabletops',     label: 'Tabletop Exercises', icon: Users, keywords: 'tabletop exercise ir gap analysis facilitator scenario red team purple' },
      { to: '/app/bcp',           label: 'BCP / DR Register',  icon: Boxes, keywords: 'bcp dr business continuity disaster recovery rto rpo tier backup' },
      { to: '/app/service-readmes', label: 'Service READMEs',  icon: FileText, keywords: 'techdocs readme markdown service documentation aggregator backstage' },
      { to: '/app/status',        label: 'Status Page',        icon: Activity, keywords: 'uptime public service status board health' },
      { to: '/app/leavers',       label: 'Leavers',            icon: Users, keywords: 'offboarding hris workday bamboohr access revocation' },
    ],
  },
  // ─── AI surface — single parent with subgroup headers ───────────────
  {
    id: 'ai', title: 'AI', items: [
      // ─── Overview ───
      { to: '/app/frontier-index',     label: 'Frontier Index',       icon: Sparkles, subgroup: 'Overview', keywords: 'index summary directory totals all catalogues frontier intelligence dashboard' },
      { to: '/app/frontier-timeline',  label: 'Frontier Timeline',    icon: Activity, subgroup: 'Overview', keywords: 'timeline chronological feed unified merged all catalogues newest events' },
      { to: '/app/frontier-editor',    label: 'Frontier Editor',      icon: Pencil,   subgroup: 'Overview', keywords: 'admin edit crud catalogues add new entry frontier' },

      // ─── Models ───
      { to: '/app/frontier-models',    label: 'Frontier Models (orgs)', icon: Brain,  subgroup: 'Models', keywords: 'mythos anthropic openai deepmind meta mistral cohere xai stability research labs sota' },
      { to: '/app/frontier-globe',     label: 'Frontier Globe',       icon: Globe,    subgroup: 'Models', keywords: 'labs world map ai research arcs frontier' },
      { to: '/app/notable-models',     label: 'Notable Models (Epoch live)', icon: Database, subgroup: 'Models', keywords: 'epoch ai notable frontier registry compute parameters training run' },
      { to: '/app/frontier-rankings',  label: 'Frontier Rankings',    icon: Trophy,   subgroup: 'Models', keywords: 'leaderboard ranking frontier compute params recency lab country sortable' },
      { to: '/app/frontier-pricing',   label: 'Model Pricing ($/Mtok)', icon: DollarSign, subgroup: 'Models', keywords: 'pricing cost dollars per million tokens openai anthropic claude gpt input output' },
      { to: '/app/model-releases',     label: 'Model Releases (curated)', icon: Rocket, subgroup: 'Models', keywords: 'gpt claude gemini llama frontier launch timeline chronological release date version' },
      { to: '/app/agents',             label: 'Agent Console',         icon: Sparkles, subgroup: 'Models', keywords: 'copilot foundry openai studio' },
      { to: '/app/ai-models',          label: 'AI Models (legacy)',    icon: Trophy,   subgroup: 'Models', keywords: 'llm leaderboard benchmarks' },
      { to: '/app/ai-costs',           label: 'AI Cost Calc',          icon: Calculator, subgroup: 'Models', keywords: 'tokens pricing' },

      // ─── Research ───
      { to: '/app/trending-papers',    label: 'Trending Papers (HF live)', icon: FileText, subgroup: 'Research', keywords: 'huggingface daily arxiv research papers community upvoted trending' },
      { to: '/app/arxiv',              label: 'arXiv Firehose (live)', icon: BookOpen, subgroup: 'Research', keywords: 'arxiv cs.ai cs.cl cs.lg recent papers raw firehose unfiltered' },
      { to: '/app/benchmarks',         label: 'Capability Benchmarks', icon: Trophy,    subgroup: 'Research', keywords: 'benchmarks sota leaderboard arc agi swe bench gpqa mmlu hle aime humaneval' },
      { to: '/app/knowledge-base',     label: 'Knowledge Base',        icon: Users,     subgroup: 'Research', keywords: 'people profiles threat models books canon projections forecasts altman amodei hinton bengio sutskever scheming alignment mesa-optimisation papers datasets evals robotics scientific jailbreaks cyber education healthcare' },
      { to: '/app/ai-insights',        label: 'AI Insights',           icon: Brain,     subgroup: 'Research', keywords: 'summarise' },
      { to: '/app/ai-adoption',        label: 'AI Adoption',           icon: Brain,     subgroup: 'Research', keywords: 'industry' },

      // ─── Industry ───
      { to: '/app/industry-watch',     label: 'Industry Watch',        icon: Gavel,     subgroup: 'Industry', keywords: 'lawsuits departures open letters funding compute datacenter buildouts nyt openai musk hinton leike sutskever stargate acquisitions conferences oss chips energy bans testimonies aisi evals graveyard clusters agents defence data deals hypergrowth' },

      // ─── Safety + Governance ───
      { to: '/app/ai-risk-live',       label: 'AI Risk Index (Live)',  icon: Activity,  subgroup: 'Safety + Governance', keywords: 'stanford hai ai index live yoy incidents sector banking financial frontier risk index' },
      { to: '/app/ai-incidents',       label: 'AI Safety Incidents',   icon: Skull,     subgroup: 'Safety + Governance', keywords: 'jailbreak hallucination data leak alignment scheming sleeper agents tay sydney chevrolet samsung air canada incident catalog' },
      { to: '/app/frontier-regulation',label: 'Regulation Tracker',    icon: Scale,     subgroup: 'Safety + Governance', keywords: 'eu ai act uk aisi nist sb1047 china genai rules japan korea singapore frontier regulator law jurisdiction' },
      { to: '/app/national-strategies',label: 'National AI Strategies', icon: Globe,    subgroup: 'Safety + Governance', keywords: 'national ai strategy stargate aria nairr eu ai office uae saudi pif india china korea sovereign' },
      { to: '/app/frontier-defence',   label: 'Frontier Defence Lab',  icon: Shield,    subgroup: 'Safety + Governance', keywords: 'jailbreak prompt injection sandbox enclave tee sgx tdx digital twin patch cycle resilience defence security' },
      { to: '/app/vigilance',          label: 'Mythos Vigilance',      icon: Eye,       subgroup: 'Safety + Governance', keywords: 'agentic ai cover tracks honeypot canary trip evasion deception scheming user agent ua bot crawler fingerprint' },
    ],
  },
  {
    id: 'intel', title: 'Intelligence & Innovation', items: [
      { to: '/app/tech-news',          label: 'Tech News',         icon: Newspaper, keywords: 'industry articles startups ai ml cloud' },
      { to: '/app/innovation',         label: 'Innovation Feed',   icon: Lightbulb },
      { to: '/app/startups',           label: 'Startup Radar',     icon: Rocket },
      { to: '/app/manda',              label: 'M&A / IPO',         icon: GitBranch },
      { to: '/app/opensource',         label: 'Open Source',       icon: GitBranch },
      { to: '/app/competitors',        label: 'Competitor Intel',  icon: Building2 },
      { to: '/app/earnings',           label: 'Earnings Calendar', icon: Clock },
      { to: '/app/talent',             label: 'Talent & Jobs',     icon: Users },
      { to: '/app/sentiment',          label: 'Social Sentiment',  icon: MessageCircle },
      { to: '/app/sentiment-tracking', label: 'Sentiment Charts',  icon: TrendingUp },
    ],
  },
  {
    id: 'trends', title: 'Trends & Radar', items: [
      { to: '/app/super-trends', label: 'Super Trends', icon: Sparkles, keywords: 'trend mining charts sparklines movers heatmap' },
      { to: '/app/hype-cycle',   label: 'Hype Cycle',   icon: Activity },
      { to: '/app/tech-radar',   label: 'Tech Radar',   icon: Target, keywords: 'thoughtworks adopt trial assess hold blip quadrant' },
      { to: '/app/time-machine', label: 'Time-machine', icon: Clock, keywords: 'history snapshots scrub backwards trend' },
    ],
  },
  {
    id: 'infra', title: 'Cloud & Infra', items: [
      { to: '/app/cloud',           label: 'Cloud & M365',     icon: Cloud },
      { to: '/app/internet-health', label: 'Internet Health',  icon: Wifi },
      { to: '/app/world',           label: 'World Monitor',    icon: Globe, keywords: 'geopolitics events cables datacentres' },
      { to: '/app/outage-history',  label: 'Outage History',   icon: Clock },
      { to: '/app/supply-chain',    label: 'Supply Chain',     icon: Package },
      { to: '/app/energy',          label: 'Energy & Green',   icon: Zap },
      { to: '/app/pricing',         label: 'Cloud Pricing',    icon: DollarSign },
      { to: '/app/pricing-trends',  label: 'Price Trends (5yr)', icon: TrendingUp },
    ],
  },
  {
    id: 'workspace', title: 'Workspace', items: [
      { to: '/app/workspace', label: 'Workspace',   icon: LayoutGrid },
      { to: '/app/me/access', label: 'My Access',   icon: User, keywords: 'cdm rbac request role grant access self service my own attestation expiry banking grade bounded scope viewer analyst operator pending request approval audit track 5.5' },
      { to: '/app/ask',       label: 'Ask',         icon: Sparkles, keywords: 'natural language nl query sql ai search assistant' },
      { to: '/app/signals',   label: 'All Signals', icon: Radar },
      { to: '/app/terminal',  label: 'Terminal',    icon: Monitor },
      { to: '/app/notes',     label: 'Notes',       icon: StickyNote },
      { to: '/app/bookmarks', label: 'Bookmarks',   icon: Bookmark },
      { to: '/app/m',         label: 'Mobile (→ Exec)', icon: Smartphone, keywords: 'phone pocket alias redirect' },
    ],
  },
  {
    id: 'admin', title: 'Admin & System', items: [
      { to: '/app/connectors-manage', label: 'Connectors',       icon: Cable, keywords: 'executive summary sources' },
      { to: '/app/connectors-health', label: 'Connector Health', icon: Activity, keywords: 'status errors last run freshness' },
      { to: '/app/integrations',      label: 'Integrations',     icon: Plug },
      { to: '/app/feed-manager',      label: 'Feed Manager',     icon: Rss },
      { to: '/app/admin/feeds',       label: 'Feeds (Unified)',  icon: Rss, keywords: 'admin add custom rss news threat intel cdm rest sql webhook destination category routing flying penguin' },
      { to: '/app/eol-manage',        label: 'EOL Products',     icon: CalendarClock },
      { to: '/app/admin',             label: 'Users + Setup',    icon: Users, keywords: 'sso entra github grafana' },
      { to: '/app/settings',          label: 'Settings',         icon: Settings },
      { to: '/app/api-docs',          label: 'API Docs',         icon: Code },
      { to: '/app/wiki',              label: 'Knowledge Base',   icon: BookOpen },
      { to: '/app/changelog',         label: 'Changelog',        icon: GitBranch, keywords: 'commits release history what shipped' },
      { to: '/app/activity',          label: 'Activity Log',     icon: ClipboardList },
      { to: '/app/savings',           label: 'Headcount Avoided', icon: Users, keywords: 'fte hours saved analyst leaderboard agent digital ai value margin headcount avoided' },
      { to: '/app/admin/nav-visibility', label: 'Nav Visibility', icon: EyeOff, keywords: 'hide menu item admin global sidebar visibility disable' },
      { to: '/app/admin/credentials',    label: 'Credentials',    icon: Key,    keywords: 'api keys env secrets vulncheck threatfox tokens checklist post deploy' },
      { to: '/app/admin/access',         label: 'Access Grid',    icon: Lock,   keywords: 'cdm rbac role grants attestation governance bounded expires audit who has what scope viewer analyst operator admin global region home org cascade explicit grant cdm_role_grants admin track 5.1' },
      { to: '/app/admin/attestation',    label: 'Attestation',    icon: ClipboardCheck, keywords: 'cdm campaign recertification line manager legal owner control function security admin negative attestation suspend revoke keep reduce escalate evidence days dormant my queue admin track 5.3 cdm_attestation_campaigns cdm_attestation_items' },
      { to: '/app/admin/jml',            label: 'JML Inbox',      icon: Users,  keywords: 'joiner mover leaver hr feed offboarding onboarding revoke disable account person leavers movers cdm_persons cdm_jml_events transition window kill list admin track 5.4' },
      { to: '/app/admin/orgs',           label: 'Org Tree',       icon: Building2, keywords: 'cdm orgs hierarchy entity business unit ring fence ringfence isolation jurisdiction regulatory zone data residency parent child reparent admin track 5.2 cdm_orgs' },
      { to: '/app/admin/underwriting-pricing', label: 'Underwriting Pricing', icon: DollarSign, keywords: 'cyber insurance broker pulse marsh howden aon ciab lockton gallagher wtw lloyds premium rate quarterly yoy qoq netdiligence allianz amber verizon dbir benchmark loss distribution claims median ransom paid bi loss admin paste in pricing' },
      { to: '/app/admin/system-health', label: 'System Health', icon: Activity, keywords: 'celery worker heartbeat stale failing task ingestion poisoned event loop watchdog uptime freshness monitor may 12 2026 incident' },
      { to: '/app/help',              label: 'Help & About',     icon: HelpCircle, keywords: 'gap analysis feature availability instance config' },
    ],
  },
]

// ── Persistence helpers ──────────────────────────────────────────────────────
const LS_OPEN = 'sidenav.open.v2'  // bumped 2026-05-10: defaultOpen flipped on cyber + engineering, AI groups consolidated — invalidate stale per-group state
const LS_PIN = 'sidenav.pinned'
const LS_ORDER = 'sidenav.order.v4'  // bumped 2026-05-09: collapsed 5 AI groups into one with subgroup headers
const LS_EDIT = 'sidenav.edit'
const LS_HIDDEN = 'sidenav.hidden'  // array of `to` paths the user has hidden from the nav
const LS_SUBGROUP_OPEN = 'sidenav.subgroup_open.v1'  // 2026-05-15: per-(group, subgroup) open state — default open on first run

function loadSubgroupOpen(): Record<string, Record<string, boolean>> {
  try {
    const raw = localStorage.getItem(LS_SUBGROUP_OPEN)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') return parsed
    }
  } catch { /* ignore */ }
  return {}
}

interface StoredGroup { id: string; title: string; items: string[] }

function serialiseGroups(groups: NavGroup[]): StoredGroup[] {
  return groups.map(g => ({ id: g.id, title: g.title, items: g.items.map(i => i.to) }))
}

function deserialiseGroups(stored: StoredGroup[]): NavGroup[] {
  // Merge stored order with defaults: any new items appended to their default group.
  const allDefaultItems = new Map<string, { item: NavItem; groupId: string }>()
  DEFAULT_GROUPS.forEach(g => g.items.forEach(i => allDefaultItems.set(i.to, { item: i, groupId: g.id })))
  const storedToKeys = new Set<string>(stored.flatMap(g => g.items))

  const groups: NavGroup[] = stored.map(sg => ({
    id: sg.id,
    title: sg.title,
    items: sg.items
      .map(to => allDefaultItems.get(to)?.item)
      .filter((i): i is NavItem => !!i),
  }))

  // Append any new items (added to the app after the user last saved) into their
  // default group — or into a fallback "New" section at the end.
  const missing = Array.from(allDefaultItems.entries()).filter(([to]) => !storedToKeys.has(to))
  if (missing.length) {
    for (const [_, rec] of missing) {
      const target = groups.find(g => g.id === rec.groupId)
      if (target) {
        target.items.push(rec.item)
      } else {
        // Group also missing — create it
        const defG = DEFAULT_GROUPS.find(g => g.id === rec.groupId)
        groups.push({
          id: rec.groupId,
          title: defG?.title ?? rec.groupId,
          items: [rec.item],
        })
      }
    }
  }
  return groups
}

function loadGroups(): NavGroup[] {
  try {
    const raw = localStorage.getItem(LS_ORDER)
    if (raw) {
      const parsed: StoredGroup[] = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length) return deserialiseGroups(parsed)
    }
  } catch { /* ignore */ }
  return DEFAULT_GROUPS
}

function loadOpenState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(LS_OPEN)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return Object.fromEntries(DEFAULT_GROUPS.map(g => [g.id, !!g.defaultOpen]))
}

function loadPinned(): string[] {
  try {
    const raw = localStorage.getItem(LS_PIN)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return ['/app', '/app/executive', '/app/risk-matrix', '/app/services', '/app/cyber-warroom', '/app/countdowns']
}

function loadHidden(): string[] {
  try {
    const raw = localStorage.getItem(LS_HIDDEN)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) return parsed
    }
  } catch { /* ignore */ }
  return []
}

// ── Styles ──────────────────────────────────────────────────────────────────
const linkBase = 'flex items-center gap-2 px-2 py-1 rounded-md text-xs transition-colors duration-150'
const linkIdle = 'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
const linkActive = 'bg-blue/10 text-blue-light border border-blue/20'

// ── Drag state (module-scoped so it survives renders) ────────────────────────
type DragKind = 'group' | 'item'
interface DragState {
  kind: DragKind
  groupId: string    // source group id
  itemTo?: string    // for item drags
}
let currentDrag: DragState | null = null

// ── Item row ─────────────────────────────────────────────────────────────────
function Item({
  item, groupId, pinned, onTogglePin, editMode, onDropBefore,
  hidden, onToggleHidden,
}: {
  item: NavItem
  groupId: string
  pinned: boolean
  onTogglePin: (to: string) => void
  editMode: boolean
  onDropBefore: (fromGroup: string, fromTo: string, targetGroup: string, targetTo: string) => void
  hidden: boolean
  onToggleHidden: (to: string) => void
}) {
  const [over, setOver] = useState(false)
  return (
    <div
      className={clsx(
        'flex items-center gap-0.5 group relative',
        over && 'bg-blue/5',
        hidden && editMode && 'opacity-40',
      )}
      draggable={editMode}
      onDragStart={editMode ? (e) => {
        currentDrag = { kind: 'item', groupId, itemTo: item.to }
        e.dataTransfer.effectAllowed = 'move'
      } : undefined}
      onDragOver={editMode ? (e) => {
        if (currentDrag?.kind === 'item') { e.preventDefault(); setOver(true) }
      } : undefined}
      onDragLeave={editMode ? () => setOver(false) : undefined}
      onDrop={editMode ? (e) => {
        e.preventDefault()
        setOver(false)
        if (currentDrag?.kind === 'item' && currentDrag.itemTo) {
          onDropBefore(currentDrag.groupId, currentDrag.itemTo, groupId, item.to)
        }
        currentDrag = null
      } : undefined}
    >
      {editMode && (
        <GripVertical size={10} className="text-text-muted cursor-grab shrink-0" />
      )}
      <NavLink
        to={item.to}
        end={item.end}
        onClick={editMode ? (e) => e.preventDefault() : undefined}
        className={({ isActive }) => clsx(linkBase, 'flex-1 min-w-0', isActive && !editMode ? linkActive : linkIdle)}
      >
        <item.icon size={14} className="shrink-0" />
        <span className="truncate">{item.label}</span>
      </NavLink>
      {!editMode && (
        <button
          type="button"
          onClick={e => { e.preventDefault(); onTogglePin(item.to) }}
          className={clsx(
            'p-1 rounded-md transition-opacity shrink-0',
            pinned ? 'opacity-100 text-amber' : 'opacity-0 group-hover:opacity-60 text-text-muted hover:text-amber hover:opacity-100'
          )}
          title={pinned ? 'Unpin' : 'Pin to favourites'}
          aria-label={pinned ? 'Unpin' : 'Pin'}
        >
          <Star size={11} fill={pinned ? 'currentColor' : 'none'} />
        </button>
      )}
      {editMode && (
        <button
          type="button"
          onClick={e => { e.preventDefault(); onToggleHidden(item.to) }}
          className={clsx(
            'p-1 rounded-md shrink-0',
            hidden ? 'text-text-muted hover:text-emerald' : 'text-text-muted hover:text-rose',
          )}
          title={hidden ? 'Show in menu' : 'Hide from menu'}
          aria-label={hidden ? 'Show' : 'Hide'}
        >
          {hidden ? <EyeOff size={11} /> : <Eye size={11} />}
        </button>
      )}
    </div>
  )
}

// ── Main nav ─────────────────────────────────────────────────────────────────
export default function SideNav({ collapsed }: { collapsed: boolean }) {
  const [groups, setGroups] = useState<NavGroup[]>(loadGroups)
  const [open, setOpen] = useState<Record<string, boolean>>(loadOpenState)
  const [pinned, setPinned] = useState<string[]>(loadPinned)
  const [hidden, setHidden] = useState<string[]>(loadHidden)
  const [globalHidden, setGlobalHidden] = useState<string[]>([])
  const [query, setQuery] = useState('')
  const [editMode, setEditMode] = useState<boolean>(localStorage.getItem(LS_EDIT) === 'true')
  const [groupOver, setGroupOver] = useState<string | null>(null)
  const [subgroupOpen, setSubgroupOpen] = useState<Record<string, Record<string, boolean>>>(loadSubgroupOpen)

  useEffect(() => { localStorage.setItem(LS_SUBGROUP_OPEN, JSON.stringify(subgroupOpen)) }, [subgroupOpen])
  useEffect(() => { localStorage.setItem(LS_OPEN, JSON.stringify(open)) }, [open])
  useEffect(() => { localStorage.setItem(LS_PIN, JSON.stringify(pinned)) }, [pinned])
  useEffect(() => { localStorage.setItem(LS_HIDDEN, JSON.stringify(hidden)) }, [hidden])
  useEffect(() => { localStorage.setItem(LS_ORDER, JSON.stringify(serialiseGroups(groups))) }, [groups])
  useEffect(() => { localStorage.setItem(LS_EDIT, String(editMode)) }, [editMode])

  // Pull the admin-set hidden list on mount. Silently no-ops on failure so a
  // broken admin endpoint can never blank out the whole sidebar.
  // Also auto-hides /app/savings until the headcount-avoided number crosses
  // 1 FTE — sub-1 FTE doesn't deserve a permanent menu slot. Re-checks every
  // 5 min so the entry appears the moment the threshold is crossed.
  useEffect(() => {
    let cancelled = false
    const refreshGlobal = () => {
      Promise.allSettled([
        fetch('/api/admin/nav/hidden').then(r => r.ok ? r.json() : null),
        fetch('/api/savings/summary').then(r => r.ok ? r.json() : null),
      ]).then(([navRes, savRes]) => {
        if (cancelled) return
        const fromAdmin: string[] = navRes.status === 'fulfilled' && navRes.value?.paths
          ? navRes.value.paths : []
        const fte: number = savRes.status === 'fulfilled' && savRes.value?.fte_equivalent
          ? Number(savRes.value.fte_equivalent) : 0
        const next = [...fromAdmin]
        if (fte < 1 && !next.includes('/app/savings')) next.push('/app/savings')
        setGlobalHidden(next)
      })
    }
    refreshGlobal()
    const id = setInterval(refreshGlobal, 300_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  const allItems = useMemo(() => groups.flatMap(g => g.items), [groups])
  const itemByTo = useMemo(
    () => Object.fromEntries(allItems.map(i => [i.to, i])) as Record<string, NavItem>,
    [allItems]
  )
  // Effective hidden = union of admin-global + per-user local.
  const hiddenSet = useMemo(() => new Set([...hidden, ...globalHidden]), [hidden, globalHidden])

  const toggleGroup = (id: string) => setOpen(prev => ({ ...prev, [id]: !prev[id] }))
  const togglePin = (to: string) => setPinned(prev => prev.includes(to) ? prev.filter(x => x !== to) : [...prev, to])
  const toggleHidden = (to: string) => setHidden(prev => prev.includes(to) ? prev.filter(x => x !== to) : [...prev, to])
  const toggleSubgroup = (groupId: string, sg: string) => setSubgroupOpen(prev => {
    const g = prev[groupId] ?? {}
    const currentlyOpen = g[sg] !== false  // default open
    return { ...prev, [groupId]: { ...g, [sg]: !currentlyOpen } }
  })
  const isSubgroupOpen = (groupId: string, sg: string): boolean => {
    return subgroupOpen[groupId]?.[sg] !== false  // default open
  }
  const expandAll = () => {
    setOpen(Object.fromEntries(groups.map(g => [g.id, true])))
    setSubgroupOpen({})  // wipe = back to all-open default
  }
  const collapseAll = () => {
    setOpen(Object.fromEntries(groups.map(g => [g.id, false])))
    // Leave subgroup state alone on collapse-all — parent collapse already hides everything
  }
  const allOpen = useMemo(
    () => groups.every(g => (open[g.id] ?? !!g.defaultOpen)),
    [groups, open],
  )

  const resetLayout = () => {
    if (!confirm('Reset menu order to defaults?')) return
    localStorage.removeItem(LS_ORDER)
    setGroups(DEFAULT_GROUPS)
  }

  // Move group before another group
  const reorderGroup = (fromId: string, beforeId: string) => {
    if (fromId === beforeId) return
    setGroups(prev => {
      const fromIdx = prev.findIndex(g => g.id === fromId)
      const toIdx = prev.findIndex(g => g.id === beforeId)
      if (fromIdx < 0 || toIdx < 0) return prev
      const next = prev.slice()
      const [moved] = next.splice(fromIdx, 1)
      const adjusted = next.findIndex(g => g.id === beforeId)
      next.splice(adjusted, 0, moved)
      return next
    })
  }

  // Move item before another item (may cross groups)
  const reorderItem = (fromGroup: string, fromTo: string, targetGroup: string, targetTo: string) => {
    if (fromTo === targetTo && fromGroup === targetGroup) return
    setGroups(prev => {
      const next = prev.map(g => ({ ...g, items: g.items.slice() }))
      const src = next.find(g => g.id === fromGroup)
      const dst = next.find(g => g.id === targetGroup)
      if (!src || !dst) return prev
      const idx = src.items.findIndex(i => i.to === fromTo)
      if (idx < 0) return prev
      const [moved] = src.items.splice(idx, 1)
      const targetIdx = dst.items.findIndex(i => i.to === targetTo)
      dst.items.splice(targetIdx < 0 ? dst.items.length : targetIdx, 0, moved)
      return next
    })
  }

  // Drop on group header = move item to end of that group
  const dropItemOnGroup = (fromGroup: string, fromTo: string, targetGroup: string) => {
    if (fromGroup === targetGroup) return
    setGroups(prev => {
      const next = prev.map(g => ({ ...g, items: g.items.slice() }))
      const src = next.find(g => g.id === fromGroup)
      const dst = next.find(g => g.id === targetGroup)
      if (!src || !dst) return prev
      const idx = src.items.findIndex(i => i.to === fromTo)
      if (idx < 0) return prev
      const [moved] = src.items.splice(idx, 1)
      dst.items.push(moved)
      return next
    })
  }

  const pinnedItems = useMemo(
    () => pinned.map(to => itemByTo[to]).filter((i): i is NavItem => !!i && !hiddenSet.has(i.to)),
    [pinned, itemByTo, hiddenSet]
  )

  // True (un-filtered, un-hidden) item count per group — used by the badge
  // beside each group title so users see the full size even during a search.
  const groupCounts = useMemo(
    () => Object.fromEntries(
      groups.map(g => [g.id, g.items.filter(i => !hiddenSet.has(i.to)).length])
    ) as Record<string, number>,
    [groups, hiddenSet],
  )

  // Search filter
  const q = query.trim().toLowerCase()
  const filtered = useMemo(() => {
    if (!q) return groups
    return groups
      .map(g => ({
        ...g,
        items: g.items.filter(i =>
          i.label.toLowerCase().includes(q)
          || (i.keywords || '').toLowerCase().includes(q)
          || i.to.toLowerCase().includes(q)
        ),
      }))
      .filter(g => g.items.length > 0)
  }, [q, groups])

  // COLLAPSED rail
  if (collapsed) {
    const iconsToShow = pinnedItems.length > 0
      ? [...pinnedItems, ...allItems.filter(i => !pinned.includes(i.to))]
      : allItems
    return (
      <nav className="flex flex-col items-center gap-0.5 py-3">
        {iconsToShow.map(item => {
          const isPinned = pinned.includes(item.to)
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                clsx(
                  'p-2 rounded-lg transition-colors relative',
                  isActive ? 'bg-blue/10 text-blue-light' : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
                )
              }
              title={item.label}
            >
              <item.icon size={16} />
              {isPinned && <span className="absolute top-0 right-0 w-1.5 h-1.5 rounded-full bg-amber" />}
            </NavLink>
          )
        })}
        {/* Theme switcher visible even when the rail is collapsed */}
        <div className="mt-2 pt-2 border-t border-border w-full flex justify-center">
          <ThemeSwitcher compact />
        </div>
      </nav>
    )
  }

  // EXPANDED
  return (
    <nav className="flex flex-col py-2 px-2">
      {/* Search + edit toggle */}
      <div className="flex items-center gap-1 mb-2">
        <div className="relative flex-1">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={editMode ? 'Disable edit mode to search' : 'Search pages…'}
            disabled={editMode}
            className="w-full pl-7 pr-6 py-1.5 text-[11px] bg-surface-raised border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-blue/40 disabled:opacity-50"
            aria-label="Search pages"
          />
          {query && !editMode && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary p-0.5"
              aria-label="Clear search"
            >
              <X size={11} />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => { setEditMode(v => !v); setQuery('') }}
          className={clsx(
            'p-1.5 rounded-md border transition-colors',
            editMode
              ? 'bg-amber/10 border-amber/40 text-amber'
              : 'bg-surface-raised border-border text-text-muted hover:text-text-secondary',
          )}
          title={editMode ? 'Exit edit mode' : 'Edit menu order (drag to reorder)'}
          aria-label="Edit menu order"
        >
          {editMode ? <Unlock size={12} /> : <Lock size={12} />}
        </button>
        {editMode && (
          <button
            type="button"
            onClick={resetLayout}
            className="p-1.5 rounded-md border border-border text-text-muted hover:text-rose"
            title="Reset to default order"
            aria-label="Reset order"
          >
            <RotateCcw size={12} />
          </button>
        )}
      </div>

      {editMode && (
        <div className="text-[10px] text-amber-light mb-2 px-1">
          Drag groups + items to reorder. Saved locally.
        </div>
      )}

      {/* Expand-all / Collapse-all toggle (session-only, not persisted) */}
      {!q && !editMode && (
        <div className="flex items-center justify-end gap-0.5 mb-1 px-1">
          <button
            type="button"
            onClick={expandAll}
            disabled={allOpen}
            className={clsx(
              'p-0.5 rounded text-text-muted hover:text-text-secondary disabled:opacity-30 disabled:hover:text-text-muted transition-colors',
            )}
            title="Expand all groups"
            aria-label="Expand all groups"
          >
            <ChevronDown size={12} />
          </button>
          <button
            type="button"
            onClick={collapseAll}
            className="p-0.5 rounded text-text-muted hover:text-text-secondary transition-colors"
            title="Collapse all groups"
            aria-label="Collapse all groups"
          >
            <ChevronRight size={12} />
          </button>
        </div>
      )}

      {/* Pinned */}
      {!q && !editMode && pinnedItems.length > 0 && (
        <div className="mb-2">
          <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold text-amber">
            <Star size={10} fill="currentColor" />
            Pinned
          </div>
          <div className="flex flex-col gap-px mt-0.5">
            {pinnedItems.map(i => (
              <Item
                key={`p-${i.to}`}
                item={i}
                groupId="__pinned__"
                pinned={true}
                onTogglePin={togglePin}
                editMode={false}
                onDropBefore={reorderItem}
                hidden={hiddenSet.has(i.to)}
                onToggleHidden={toggleHidden}
              />
            ))}
          </div>
        </div>
      )}

      {/* Groups */}
      {filtered.map(group => {
        const isOpen = q ? true : (open[group.id] ?? true)
        const overThis = groupOver === group.id
        return (
          <div
            key={group.id}
            className={clsx('mb-1 rounded', overThis && 'bg-blue/5 outline-1 outline outline-blue/30')}
            draggable={editMode}
            onDragStart={editMode ? (e) => {
              currentDrag = { kind: 'group', groupId: group.id }
              e.dataTransfer.effectAllowed = 'move'
            } : undefined}
            onDragOver={editMode ? (e) => {
              e.preventDefault()
              setGroupOver(group.id)
            } : undefined}
            onDragLeave={editMode ? () => setGroupOver(null) : undefined}
            onDrop={editMode ? (e) => {
              e.preventDefault()
              setGroupOver(null)
              if (!currentDrag) return
              if (currentDrag.kind === 'group' && currentDrag.groupId !== group.id) {
                reorderGroup(currentDrag.groupId, group.id)
              } else if (currentDrag.kind === 'item' && currentDrag.itemTo) {
                dropItemOnGroup(currentDrag.groupId, currentDrag.itemTo, group.id)
              }
              currentDrag = null
            } : undefined}
          >
            <button
              type="button"
              onClick={() => !q && !editMode && toggleGroup(group.id)}
              disabled={!!q}
              className={clsx(
                'flex items-center gap-1.5 w-full px-2 py-1 text-[10px] uppercase tracking-wider font-semibold transition-colors',
                q ? 'text-text-muted cursor-default' : editMode ? 'text-amber cursor-grab' : 'text-text-muted hover:text-text-secondary',
              )}
              // eslint-disable-next-line jsx-a11y/aria-proptypes
              aria-expanded={isOpen}
            >
              {editMode ? (
                <GripVertical size={11} className="shrink-0" />
              ) : q ? (
                <span className="w-2.5 shrink-0" />
              ) : isOpen ? (
                <ChevronDown size={10} />
              ) : (
                <ChevronRight size={10} />
              )}
              <span>{group.title}</span>
              <span
                className="ml-auto text-[9px] font-normal lowercase tracking-normal px-1.5 py-px rounded-full bg-surface-raised/60 text-text-muted/80 group-hover:text-text-secondary"
                aria-label={`${groupCounts[group.id] ?? group.items.length} items`}
              >
                {groupCounts[group.id] ?? group.items.length}
              </span>
            </button>

            {isOpen && (
              <div className="flex flex-col gap-px mt-0.5">
                {(() => {
                  const visibleItems = group.items.filter(item => editMode || !hiddenSet.has(item.to))
                  // While searching or in edit mode, suppress subgroup headers so the
                  // user sees a flat list of matches / drag targets.
                  const showSubgroups = !q && !editMode
                  const out: React.ReactNode[] = []
                  let prevSubgroup: string | undefined = undefined
                  let isFirstSubgroup = true
                  let currentSubgroupCollapsed = false  // when true, skip emitting Item rows
                  visibleItems.forEach((item, idx) => {
                    if (showSubgroups && item.subgroup && item.subgroup !== prevSubgroup) {
                      const sgCount = visibleItems.filter(i => i.subgroup === item.subgroup).length
                      const sgOpen = isSubgroupOpen(group.id, item.subgroup)
                      const sgLabel = item.subgroup
                      out.push(
                        <button
                          key={`__sg-${group.id}-${sgLabel}-${idx}`}
                          type="button"
                          onClick={() => toggleSubgroup(group.id, sgLabel)}
                          className={`flex items-center gap-1.5 w-full px-2 pt-2.5 pb-1 text-[10px] uppercase tracking-wider text-text-secondary font-semibold select-none hover:text-text-primary transition-colors ${isFirstSubgroup ? '' : 'mt-1 border-t border-border/40 pt-2.5'}`}
                          aria-expanded={sgOpen ? 'true' : 'false'}
                          aria-label={`${sgOpen ? 'Collapse' : 'Expand'} ${sgLabel}`}
                        >
                          {sgOpen
                            ? <ChevronDown size={10} className="flex-shrink-0 opacity-70" />
                            : <ChevronRight size={10} className="flex-shrink-0 opacity-70" />}
                          <span className="flex-1 text-left">{sgLabel}</span>
                          <span className="text-[9px] font-normal text-text-muted/80 tabular-nums">({sgCount})</span>
                        </button>
                      )
                      prevSubgroup = sgLabel
                      isFirstSubgroup = false
                      currentSubgroupCollapsed = !sgOpen
                    } else if (showSubgroups && !item.subgroup) {
                      // an unlabelled item resets the subgroup tracking so the next
                      // labelled run will re-emit its header.
                      prevSubgroup = undefined
                      currentSubgroupCollapsed = false
                    }

                    // Skip the item if its parent subgroup is collapsed
                    if (showSubgroups && item.subgroup && currentSubgroupCollapsed) {
                      return
                    }
                    out.push(
                      <Item
                        key={item.to}
                        item={item}
                        groupId={group.id}
                        pinned={pinned.includes(item.to)}
                        onTogglePin={togglePin}
                        editMode={editMode}
                        onDropBefore={reorderItem}
                        hidden={hiddenSet.has(item.to)}
                        onToggleHidden={toggleHidden}
                      />
                    )
                  })
                  return out
                })()}
              </div>
            )}
          </div>
        )
      })}

      {q && filtered.length === 0 && (
        <div className="px-2 py-4 text-[11px] text-text-muted text-center">
          No matches for "{query}"
        </div>
      )}

      {/* Theme switcher — sits at the bottom of the expanded nav */}
      <div className="mt-4 pt-3 border-t border-border px-1">
        <ThemeSwitcher />
      </div>
    </nav>
  )
}
